import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { S, SERIF, SANS, GoldButton } from "./slUi";
import { useSpeak, SpeakButton } from "./slSpeak";

export const HERO_IMG = {
  a: "https://static.prod-images.emergentagent.com/jobs/85b307c0-58ce-40d0-8126-3abffae3b4a9/images/99c8866f2fe9a256dad80f50a59513bbe37d5654253f8fcda65eb54b77dc059e.jpeg",
  b: "https://static.prod-images.emergentagent.com/jobs/85b307c0-58ce-40d0-8126-3abffae3b4a9/images/89d134146a87b04d446c0aa2673fc05599cb43e40ed185d7a0cb4a5070244c11.jpeg",
  c: "https://static.prod-images.emergentagent.com/jobs/85b307c0-58ce-40d0-8126-3abffae3b4a9/images/c7df52464a5ecd0df23fcdb29815400c1b54fa6451b4ea27b80437b035a0d4bb.jpeg",
};
const LINE1 = "Change the conversation you have with yourself.";
const LINE2 = "Your words. Your voice. Beautifully layered audio, created around the person you want to become.";

const Headline = ({ ink }) => (
  <>
    <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
      className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]" style={{ fontFamily: SERIF, color: ink ? S.ink : S.cream }}>{LINE1}</motion.h1>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }}
      className="mt-5 max-w-xl text-lg sm:text-xl leading-snug" style={{ fontFamily: SERIF, color: ink ? "rgba(18,28,51,0.78)" : S.dim }}>{LINE2}</motion.p>
  </>
);

// Adelaida's words appear as she speaks: the "someone is here to guide me" moment.
const Caption = ({ s, lines, ink }) => {
  const live = s.state === "playing" || s.state === "paused";
  const text = live ? lines[Math.max(0, Math.min(lines.length - 1, s.shown - 1))] : "Tell me what you'd like to work on. I'll help you from there.";
  return <motion.p key={text} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-lg sm:text-xl leading-snug" style={{ fontFamily: SERIF, color: ink ? S.ink : S.cream }}>&ldquo;{text}&rdquo;</motion.p>;
};

const Ctas = ({ s, ink, testId }) => {
  const nav = useNavigate();
  return (
    <div className="mt-8 flex flex-col sm:flex-row gap-3 [&_button]:whitespace-nowrap">
      <SpeakButton s={s} label="Meet Adelaida" testId={`${testId}-meet`} dark={ink} />
      <GoldButton onClick={() => nav("/soulumo/demo?from=launch")} testId={`${testId}-experience`}>Experience Soulumo <ArrowRight size={14} /></GoldButton>
    </div>
  );
};

// A — Adelaida in the light: she is in the photograph, welcoming you into a luminous room.
export const HeroA = ({ g, testId = "hero-a" }) => {
  const s = useSpeak("opening");
  return (
    <section className="relative min-h-[100svh] overflow-hidden flex items-end sm:items-center" style={{ background: S.ivory }} data-testid={testId}>
      <motion.img src={HERO_IMG.a} alt="Adelaida welcoming you in morning light" initial={{ scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 12 }} className="absolute inset-0 w-full h-full object-cover object-[78%_center]" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(245,238,223,0.92) 0%, rgba(245,238,223,0.7) 38%, rgba(245,238,223,0) 62%), linear-gradient(0deg, rgba(245,238,223,0.85) 0%, rgba(245,238,223,0) 35%)" }} />
      <div className="relative px-6 sm:px-10 lg:px-20 pb-14 pt-28 max-w-3xl">
        <p className="text-[0.66rem] uppercase tracking-[0.5em]" style={{ fontFamily: SANS, color: S.bronze }}>Soulumo</p>
        <div className="mt-6"><Headline ink /></div>
        <div className="mt-8 max-w-md border-l-2 pl-4" style={{ borderColor: S.gold }}>
          <p className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ fontFamily: SANS, color: S.bronze }}>Adelaida · your Soulumo guide</p>
          <div className="mt-2"><Caption s={s} lines={g.opening_lines} ink /></div>
        </div>
        <Ctas s={s} ink testId={testId} />
      </div>
    </section>
  );
};

// B — Your guide, front and centre: Soulumo navy on the left, Adelaida large and luminous on the right.
export const HeroB = ({ g, testId = "hero-b" }) => {
  const s = useSpeak("opening");
  return (
    <section className="relative min-h-[100svh] grid lg:grid-cols-[1.05fr,1fr] overflow-hidden" style={{ background: S.night }} data-testid={testId}>
      <div className="relative order-2 lg:order-1 flex flex-col justify-center px-6 sm:px-10 lg:px-20 py-14 lg:py-24">
        <p className="text-[0.66rem] uppercase tracking-[0.5em]" style={{ fontFamily: SANS, color: S.gold }}>Soulumo</p>
        <div className="mt-6"><Headline /></div>
        <Ctas s={s} testId={testId} />
      </div>
      <div className="relative order-1 lg:order-2 min-h-[58svh] lg:min-h-full">
        <div className="absolute inset-0 lg:inset-y-10 lg:left-0 lg:right-10 overflow-hidden lg:rounded-t-[999px]" style={{ boxShadow: "0 0 120px rgba(224,169,122,0.35)" }}>
          <motion.img src={HERO_IMG.b} alt="Adelaida, your Soulumo guide" initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 12 }} className="w-full h-full object-cover object-top" />
        </div>
        <div className="absolute left-4 right-4 bottom-4 lg:left-[-3rem] lg:right-auto lg:bottom-20 lg:max-w-sm rounded-2xl p-5" style={{ background: "rgba(13,24,48,0.72)", backdropFilter: "blur(18px)", border: `1px solid ${S.goldSoft}` }}>
          <p className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ fontFamily: SANS, color: "#F0CFA9" }}>Adelaida · your Soulumo guide</p>
          <div className="mt-2"><Caption s={s} lines={g.opening_lines} /></div>
        </div>
      </div>
    </section>
  );
};

// C — A new beginning: a luminous morning by the sea, with Adelaida as a glass guide card.
export const HeroC = ({ g, testId = "hero-c" }) => {
  const s = useSpeak("opening");
  return (
    <section className="relative min-h-[100svh] overflow-hidden flex flex-col justify-between" style={{ background: "#EAF0F2" }} data-testid={testId}>
      <motion.img src={HERO_IMG.c} alt="A new morning by the sea" initial={{ scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 12 }} className="absolute inset-0 w-full h-full object-cover object-[70%_center]" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(250,246,238,0.88) 0%, rgba(250,246,238,0.45) 38%, rgba(250,246,238,0) 60%, rgba(7,13,29,0.35) 100%)" }} />
      <div className="relative px-6 sm:px-10 lg:px-20 pt-24 sm:pt-28 max-w-3xl">
        <p className="text-[0.66rem] uppercase tracking-[0.5em]" style={{ fontFamily: SANS, color: S.bronze }}>Soulumo</p>
        <div className="mt-6"><Headline ink /></div>
      </div>
      <div className="relative px-6 sm:px-10 lg:px-20 pb-10 flex justify-start lg:justify-end">
        <div className="w-full max-w-xl rounded-3xl p-5" style={{ background: "rgba(13,24,48,0.6)", backdropFilter: "blur(20px)", border: "1px solid rgba(245,236,217,0.25)" }}>
          <div className="flex gap-4 items-start">
            <img src={HERO_IMG.b} alt="Adelaida" className="w-20 h-24 rounded-2xl object-cover object-top shrink-0" />
            <div className="min-w-0">
              <p className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ fontFamily: SANS, color: "#F0CFA9" }}>Adelaida · your guide</p>
              <div className="mt-1.5"><Caption s={s} lines={g.opening_lines} /></div>
            </div>
          </div>
          <div className="[&>div]:mt-5 [&_button]:w-full sm:[&_button]:w-auto"><Ctas s={s} testId={testId} /></div>
        </div>
      </div>
    </section>
  );
};
